import React, {useEffect, useRef, useState} from "react";
import DOMPurify from "dompurify";
import axios from "axios";
import "./css/Tearsheet.css";
import NotesAndCommentary from "./NotesAndCommentary";
import {sanitizeAndHighlight, buildHighlightTerms, sectionContainsKeyword} from "./highlightHtml";

export default function VendorTearsheet({ vendorId, contextPath, onOpenProductPopup, onGlossaryClick, highlight, mode = "profiler" }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [synopsisExpanded, setSynopsisExpanded] = useState(false);
    const [synopsisExpandAll, setSynopsisExpandAll] = useState(false);
    const [synopsisSectionCount, setSynopsisSectionCount] = useState(0);
    const [newsExpanded, setNewsExpanded] = useState(false);
    const [descriptionsExpanded, setDescriptionsExpanded] = useState(false);

    const printRef = useRef(null);
    const printJobRef = useRef({ iframe: null, timer: null, cleaned: false, onAfterPrint: null });
    const [copyStatus, setCopyStatus] = useState("");

    const [forceOpenSections, setForceOpenSections] = useState(false);
    const accent = "var(--rusty-orange, #e87722)";
    const isAltDataMode = String(mode || "").trim().toLowerCase() === "altdata";
    const tearsheetAccentVars = {
        "--rusty-orange": isAltDataMode ? "#990026" : "#e87722",
        "--rusty-orange-rgb": isAltDataMode ? "153, 0, 38" : "232, 119, 34",
    };

    const [productsExpanded, setProductsExpanded] = useState(false);

    const PRODUCTS_COLLAPSE_THRESHOLD = 12;
    const PRODUCTS_PREVIEW_COUNT = 10;


    const hasVisibleHtml = (html) => {
        const textOnly = String(html || "")
            .replace(/<br\s*\/?>/gi, "")
            .replace(/&nbsp;|&#160;/gi, "")
            .replace(/<[^>]*>/g, "")
            .trim();
        return textOnly.length > 0;
    };

    function ensureListMarkers(html) {
        const doc = new DOMParser().parseFromString(String(html || ""), "text/html");
        const body = doc.body;

        body.querySelectorAll("ul").forEach((ul) => {
            ul.classList.add("list-disc", "pl-5", "space-y-1");
            ul.style.setProperty("list-style-type", "disc", "important");
            ul.style.setProperty("padding-left", "1.25rem", "important");
            ul.style.setProperty("margin", "0.25rem 0", "important");
        });

        body.querySelectorAll("ol").forEach((ol) => {
            ol.classList.add("list-decimal", "pl-5", "space-y-1");
            ol.style.setProperty("list-style-type", "decimal", "important");
            ol.style.setProperty("padding-left", "1.25rem", "important");
            ol.style.setProperty("margin", "0.25rem 0", "important");
        });

        body.querySelectorAll("li").forEach((li) => {
            li.style.setProperty("margin", "0.15rem 0", "important");
        });

        return body.innerHTML;
    }
    const createListMarkup = (html) => {
        const decorated = ensureListMarkers(html);

        const doc = new DOMParser().parseFromString(decorated || "", "text/html");
        const tags = Array.from(doc.body.querySelectorAll("*")).map(el => el.tagName.toLowerCase());

        return sanitizeAndHighlight(decorated, highlight, {
            ALLOWED_TAGS: [...new Set([...tags, "mark", "ul", "ol", "li", "span"])],
            ALLOWED_ATTR: ["href", "target", "style", "onClick", "class", "data-term"],
        });
    };

    function parseSynopsisToBlocks(htmlString) {
        if (!htmlString || typeof htmlString !== "string") {
            return [{ type: "free", html: htmlString || "" }];
        }

        const doc = new DOMParser().parseFromString(htmlString, "text/html");
        const body = doc.body;

        const cleanText = (el) =>
            (el.textContent || "")
                .replace(/\u00a0|&nbsp;/g, " ")
                .replace(/\s+/g, " ")
                .trim();

        // Extract a title (ignoring <br>)
        const getHeaderTitle = (span) => {
            const clone = span.cloneNode(true);
            clone.querySelectorAll("br").forEach((br) => br.remove());
            return cleanText(clone);
        };

        // Heuristic: ignore underline+strong that are basically inline links like "here."
        const isAnchorOnlyDivider = (span) => {
            const hasAnchor = span.querySelector("a") !== null || !!span.closest("a");
            if (!hasAnchor) return false;

            const title = getHeaderTitle(span);
            const plain = title.toLowerCase();

            // very short / “linkish” labels shouldn't be headers
            const short = plain.replace(/[^a-z0-9]+/g, "").length <= 10;

            const commonLinky = /^(here|read more|more|learn more|link|view|details)\.?$/i.test(title);

            // If it’s just an anchor and super short (or a common link word), treat as inline, not a header
            return short || commonLinky;
        };

        const hasMeaningfulTitle = (span) => {
            const title = getHeaderTitle(span);
            // must contain at least 2 letters overall, and not be empty
            if (!title) return false;
            if (!/[A-Za-z].*[A-Za-z]/.test(title)) return false;
            // reject anchor-only dividers
            if (isAnchorOnlyDivider(span)) return false;
            return true;
        };

        // Find underline spans
        const underlineSpans = Array.from(
            body.querySelectorAll('span[style*="text-decoration" i]')
        );

        // Keep ONLY spans that are both underline + strong (inside or wrapper) and meaningful
        const headerSpans = underlineSpans.filter((span) => {
            const style = (span.getAttribute("style") || "").toLowerCase();
            if (!style.includes("underline")) return false;

            const hasStrongInside = span.querySelector("strong, b") !== null;
            const inStrongWrapper = span.closest("strong, b") !== null;
            if (!(hasStrongInside || inStrongWrapper)) return false;

            return hasMeaningfulTitle(span);
        });

        if (headerSpans.length === 0) return [{ type: "free", html: htmlString }];

        // Sort by DOM order
        headerSpans.sort((a, b) => {
            const pos = a.compareDocumentPosition(b);
            if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
            if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
            return 0;
        });

        const rangeHtml = (startSetter, endSetter) => {
            const r = doc.createRange();
            startSetter(r);
            endSetter(r);
            const frag = r.cloneContents();
            const wrap = doc.createElement("div");
            wrap.appendChild(frag);
            return wrap.innerHTML.trim();
        };

        const hasRealText = (html) =>
            (html || "")
                .replace(/<br\s*\/?>/gi, "")
                .replace(/&nbsp;|&#160;/gi, "")
                .replace(/<[^>]*>/g, "")
                .trim().length > 0;

        const blocks = [];
        const boundaryNode = (span) => span; // cut after the underline span itself

        // Content before first header
        const beforeFirst = rangeHtml(
            (r) => r.setStartBefore(body.firstChild ?? body),
            (r) => r.setEndBefore(boundaryNode(headerSpans[0]))
        );
        if (hasRealText(beforeFirst)) blocks.push({ type: "free", html: beforeFirst });

        // Sections
        for (let i = 0; i < headerSpans.length; i++) {
            const curr = headerSpans[i];
            const next = headerSpans[i + 1] || null;

            const title = getHeaderTitle(curr);
            if (!title) continue;

            let sectionHtml = rangeHtml(
                (r) => r.setStartAfter(boundaryNode(curr)),
                (r) => {
                    if (next) r.setEndBefore(boundaryNode(next));
                    else r.setEndAfter(body.lastChild ?? body);
                }
            );

            // Fallback for odd nesting
            if (!hasRealText(sectionHtml)) {
                const parent = curr.parentElement;
                if (parent) {
                    sectionHtml = rangeHtml(
                        (r) => r.setStartAfter(parent),
                        (r) => {
                            if (next) r.setEndBefore(boundaryNode(next));
                            else r.setEndAfter(body.lastChild ?? body);
                        }
                    );
                }
            }

            if (hasRealText(sectionHtml)) {
                blocks.push({ type: "section", title, html: sectionHtml });
            }
        }

        return blocks.length ? blocks : [{ type: "free", html: htmlString }];
    }

    function CollapsibleSynopsis({
        rawHtml,
        forceOpenAll = false,
        interactive = true,
        expandAll = false,
        onSectionCountChange,
        sanitize,
        highlight
    }) {
        const blocks = React.useMemo(() => parseSynopsisToBlocks(rawHtml), [rawHtml]);
        const highlightTerms = React.useMemo(() => buildHighlightTerms(highlight), [highlight]);

        const sectionIndices = React.useMemo(
            () => blocks.map((b, idx) => (b.type === "section" ? idx : -1)).filter((x) => x >= 0),
            [blocks]
        );

        const [openMap, setOpenMap] = React.useState(() =>
            Object.fromEntries(sectionIndices.map((i) => [i, false]))
        );

        React.useEffect(() => {
            // Preserve existing open sections across re-renders/scroll-driven updates.
            setOpenMap((prev) => {
                const next = {};
                sectionIndices.forEach((i) => {
                    next[i] = prev?.[i] ?? false;
                });
                return next;
            });
        }, [sectionIndices]);

        React.useEffect(() => {
            if (typeof onSectionCountChange === "function") {
                onSectionCountChange(sectionIndices.length);
            }
        }, [onSectionCountChange, sectionIndices.length]);

        React.useEffect(() => {
            if (!interactive) return;
            setOpenMap((prev) => {
                const next = {};
                sectionIndices.forEach((i) => {
                    next[i] = !!expandAll;
                });
                return next;
            });
        }, [expandAll, interactive, sectionIndices]);

        const hideSections = !interactive && !forceOpenAll;

        return (
            <div className="space-y-2">
                {blocks.map((b, idx) => {
                    if (b.type === "free") {
                        return <div
                            key={`free-${idx}`}
                            dangerouslySetInnerHTML={sanitize(b.html)}
                        />;
                    }

                    if (hideSections) {
                        // Collapsed view: show the section body without the toggle header
                        const hasText =
                            (b.html || "")
                                .replace(/<br\s*\/?>/gi, "")
                                .replace(/&nbsp;|&#160;/gi, "")
                                .replace(/<[^>]*>/g, "")
                                .trim().length > 0;

                        if (!hasText) return null; // don't render empty sections
                        return (
                            <div
                                key={`sec-${idx}`}
                                className="mt-2"
                                dangerouslySetInnerHTML={sanitize(b.html)}
                            />
                        );
                    }

                    const isOpen = !!openMap[idx];
                    const isVisible = forceOpenAll || isOpen;
                    const containsKeyword = sectionContainsKeyword(b.html, highlightTerms);

                    return (
                        <div key={`sec-${idx}`} className="border rounded-md">
                            <button
                                type="button"
                                className={containsKeyword ? "w-full text-left px-3 py-2 font-semibold flex items-center justify-between hover:bg-gray-50 section-highlight" : "w-full text-left px-3 py-2 font-semibold flex items-center justify-between hover:bg-gray-50"}
                                onClick={() => {
                                    if (!interactive) return;
                                    setOpenMap((m) => ({ ...m, [idx]: !m[idx] }));
                                }}
                                aria-disabled={!interactive}
                            >
                                <span>{b.title}</span>
                                <span className="text-xs">
                {interactive ? (isOpen ? "Hide" : "Show") : "Expand above to toggle"}
              </span>
                            </button>
                            <div
                                className={isVisible ? "px-3 pb-3 block" : "px-3 pb-3 hidden"}
                                dangerouslySetInnerHTML={sanitize(b.html)}
                            />
                        </div>
                    );
                })}
            </div>
        );
    }

    useEffect(() => () => cleanupPrintJob(), []);

    function cleanupPrintJob() {
        const job = printJobRef.current;
        if (!job || job.cleaned) return;
        job.cleaned = true;

        try {
            const w = job.iframe?.contentWindow;
            if (w && job.onAfterPrint) w.removeEventListener('afterprint', job.onAfterPrint);
        } catch {}

        if (job.timer) {
            clearTimeout(job.timer);
            job.timer = null;
        }

        try {
            if (job.iframe && job.iframe.isConnected) {
                job.iframe.remove();
            }
        } catch {}

        job.iframe = null;
        job.onAfterPrint = null;
    }

    function printElement(el) {
        return new Promise((resolve) => {
            if (!el) return resolve();

            const iframe = document.createElement('iframe');
            Object.assign(iframe.style, { position:'fixed', right:0, bottom:0, width:'0', height:'0', border:0 });
            document.body.appendChild(iframe);

            printJobRef.current = { iframe, timer: null, cleaned: false, onAfterPrint: null };

            const doc = iframe.contentDocument || iframe.contentWindow.document;

            const headStyles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
                .map(n => n.outerHTML).join('\n');

            const html = `<!DOCTYPE html>
<html><head>
<meta charset="utf-8"/>
<base href="${document.baseURI}">
${headStyles}
<style>
  @page { size: A4; margin: 12mm; }
  .no-print { display:none !important; }
  html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .overflow-auto, .overflow-scroll { overflow: visible !important; }
</style>
</head><body>${el.outerHTML}</body></html>`;

            doc.open(); doc.write(html); doc.close();

            const w = iframe.contentWindow;

            const onAfterPrint = () => {
                cleanupPrintJob();
                resolve();
            };
            printJobRef.current.onAfterPrint = onAfterPrint;

            try { w.addEventListener('afterprint', onAfterPrint, { once: true }); } catch {}

            printJobRef.current.timer = setTimeout(() => {
                cleanupPrintJob();
                resolve();
            }, 4000);

            setTimeout(() => {
                try { w.focus(); w.print(); }
                catch { cleanupPrintJob(); resolve(); }
            }, 200);
        });
    }

    const handlePrint = () => {
        const prevSyn = synopsisExpanded;
        const prevDesc = descriptionsExpanded;
        setSynopsisExpanded(true);
        setDescriptionsExpanded(true);
        setForceOpenSections(true);
        setTimeout(() => {
            printElement(printRef.current).finally(() => {
                setSynopsisExpanded(prevSyn);
                setDescriptionsExpanded(prevDesc);
                setForceOpenSections(false);
            });
        }, 50);
    };

    const getFilenameFromDisposition = (cdHeader) => {
        if (!cdHeader) return null;
        const starMatch = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(cdHeader);
        if (starMatch) return decodeURIComponent(starMatch[1]);
        const plainMatch = /filename\s*=\s*"([^"]+)"/i.exec(cdHeader);
        if (plainMatch) return plainMatch[1];
        const bareMatch = /filename\s*=\s*([^;]+)/i.exec(cdHeader);
        if (bareMatch) return bareMatch[1].trim();
        return null;
    };

    const buildExportUrl = (format /* 'pdf' | 'docx' */) => {
        const base = `${process.env.REACT_APP_API_URL}/exports/vendors/${vendorId}`;
        const params = new URLSearchParams();
        params.set("mode", format);
        params.set("ispdf", String(format === "pdf"));
        params.set("isword", String(format === "docx"));
        return `${base}?${params.toString()}`;
    };


    const downloadFromServer = async (format /* 'pdf' | 'docx' */) => {
        try {
            const token = localStorage.getItem("token");
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const url = buildExportUrl(format);

            const res = await axios.get(url, { headers, responseType: "blob" });

            const fromHeader = getFilenameFromDisposition(res.headers["content-disposition"]);
            const fallback = `${safeFileBase}.${format === "pdf" ? "pdf" : "docx"}`;
            const filename = fromHeader || fallback;

            const blob = new Blob([res.data], {
                type:
                    format === "pdf"
                        ? "application/pdf"
                        : "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            });

            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(link.href);
        } catch (err) {
            console.error("Export failed:", err);
            alert("Sorry—couldn’t export the tearsheet. Please try again or contact support.");
        }
    };

    const copyTearsheetText = async () => {
        const text = printRef.current?.innerText?.trim() || "";
        if (!text) {
            setCopyStatus("No text");
            return;
        }
        try {
            if (navigator.clipboard?.writeText && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
            } else {
                const ta = document.createElement("textarea");
                ta.value = text;
                ta.setAttribute("readonly", "");
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.focus();
                ta.select();
                const ok = document.execCommand("copy");
                document.body.removeChild(ta);
                if (!ok) throw new Error("Copy command failed");
            }
            setCopyStatus("Copied");
        } catch (err) {
            console.error("Copy tearsheet text failed:", err);
            setCopyStatus("Copy failed");
        }
    };

    useEffect(() => {
        if (!copyStatus) return undefined;
        const timer = setTimeout(() => setCopyStatus(""), 1500);
        return () => clearTimeout(timer);
    }, [copyStatus]);

    useEffect(() => {
        if (!vendorId) return;
        let isMounted = true;
        setLoading(true);
        setError(null);

        async function fetchVendor() {
            try {
                const headers = {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                };
                const res = await axios.get(
                    `${contextPath}/vendor/${vendorId}/tearsheet`,
                    { headers, params: { mode } }
                );
                if (isMounted) setData(res.data);
            } catch (err) {
                console.error("Vendor fetch failed:", err);
                if (isMounted) setError(err);
            } finally {
                if (isMounted) setLoading(false);
            }
        }

        fetchVendor();
        return () => { isMounted = false; };
    }, [vendorId, contextPath, mode]);

    if (loading) return <div className="loading-spinner" />;
    if (error)   return <div className="p-4 text-red-600">Error: {error.message}</div>;

    const {
        vendorName,
        lastUpdated,
        parentCompanies,
        ownership,
        types,
        contactHtml,
        synopsisHtml,
        newsHtml,
        additionalInfoHtml,
        strengthsHtml,
        weaknessesHtml,
        deliveryHtml,
        descriptionsHtml,
        products = [],
    } = data;

    const linkedProducts = (products && products.length > 0)
        ? products
        : [] ;

    const productLabel = linkedProducts.length === 1 ? "Product" : "Products";
    const productWord = (n) => (n === 1 ? "product" : "products");

    const primaryProductName =
        (linkedProducts[0] && linkedProducts[0].name)|| "";

    const safeFileBase = (primaryProductName || vendorName || "tearsheet")
        .replace(/[\\/:*?"<>|]/g, "-");


    const createMarkup = (html) => {
        const doc = new DOMParser().parseFromString(html || "", "text/html");
        const tags = Array.from(doc.body.querySelectorAll("*")).map(el => el.tagName.toLowerCase());

        return sanitizeAndHighlight(html, highlight, {
            ALLOWED_TAGS: [...new Set([...tags, "mark", "span"])],
            ALLOWED_ATTR: ["href", "target", "style", "onClick", "class", "data-term"],
        });
    };

    const formattedDate = new Date(lastUpdated).toLocaleDateString("en-GB", {
        day: "2-digit", month: "2-digit", year: "numeric",
    });


    return (
        <div ref={printRef} className="Threedi-blue overflow-auto bg-white shadow rounded" onClick={onGlossaryClick} style={tearsheetAccentVars}>
            <div className="last-edited">
                Last Edited: {formattedDate}
            </div>

            <div className="p-4 pb-0 no-print">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold">
  <span className="block text-gray-500 text-sm uppercase tracking-wide">
    Vendor Tearsheet
  </span>
                        {vendorName}
                    </h2>

                    <div className="flex items-center gap-2" role="toolbar" aria-label="Export actions">
                        {/* Print (browser) */}
                        <button
                            type="button"
                            onClick={handlePrint}
                            className="inline-flex items-center justify-center w-10 h-10 rounded-full border"
                            style={{ borderColor: accent, color: accent }}
                            title="Print this tearsheet"
                            aria-label="Print this tearsheet"
                        >
                            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                                <path d="M6 9V4h12v5" />
                                <rect x="4" y="9" width="16" height="8" rx="2" ry="2" />
                                <path d="M7 17h10v3H7z" />
                            </svg>
                        </button>

                        <button
                            type="button"
                            onClick={copyTearsheetText}
                            className="inline-flex items-center justify-center w-10 h-10 rounded-full border"
                            style={{ borderColor: accent, color: accent }}
                            title="Copy tearsheet text"
                            aria-label="Copy tearsheet text"
                        >
                            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                                <rect x="9" y="9" width="11" height="11" rx="2" />
                                <path d="M6 15H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v1" />
                            </svg>
                        </button>


                        {/* Word (server) */}
                        <button
                            type="button"
                            onClick={() => downloadFromServer("docx")}
                            className="inline-flex items-center justify-center w-10 h-10 rounded-full border"
                            style={{ borderColor: accent, color: accent }}
                            title="Export Word (.docx)"
                            aria-label="Export Word (.docx)"
                        >
                            {/* File-Word icon */}
                            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                                <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z" fill="none" stroke="currentColor" strokeWidth="1.6"/>
                                <path d="M14 2v5h5" fill="none" stroke="currentColor" strokeWidth="1.6"/>
                                <g fontFamily="Segoe UI, Arial, sans-serif" fontWeight="700">
                                    <text x="7.2" y="17" fontSize="8" fill="currentColor">W</text>
                                </g>
                            </svg>
                        </button>

                        {/* PDF (server) */}
                        <button
                            type="button"
                            onClick={() => downloadFromServer("pdf")}
                            className="inline-flex items-center justify-center w-10 h-10 rounded-full border"
                            style={{ borderColor: accent, color: accent }}
                            title="Export PDF"
                            aria-label="Export PDF"
                        >
                            {/* File-PDF icon */}
                            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                                <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z" fill="none" stroke="currentColor" strokeWidth="1.6"/>
                                <path d="M14 2v5h5" fill="none" stroke="currentColor" strokeWidth="1.6"/>
                                <g fontFamily="Segoe UI, Arial, sans-serif" fontWeight="800">
                                    <text x="5.6" y="17" fontSize="6.6" letterSpacing="0.3" fill="currentColor">PDF</text>
                                </g>
                            </svg>
                        </button>
                    </div>
                </div>
                {copyStatus && (
                    <div className="mt-1 text-xs text-right text-gray-600" aria-live="polite">
                        {copyStatus}
                    </div>
                )}
            </div>

            <div className="p-4 overflow-auto">
                {parentCompanies?.length > 0 && (
                    <div className="mb-2">
            <span className="font-semibold tearsheet-heading">
              Parent Company(s):
            </span>{" "}
                        {parentCompanies.join(', ')}
                    </div>
                )}

                {ownership && (
                    <div className="mb-2">
            <span className="font-semibold tearsheet-heading">
              Ownership:
            </span> {ownership}
                    </div>
                )}

                {types && (
                    <div className="mb-2">
            <span className="font-semibold tearsheet-heading">
              Vendor Type:
            </span> {types.join(', ')}
                    </div>
                )}


                {/* Product(s) Name(s) */}
                {linkedProducts.length > 0 && (() => {
                    const shouldCollapse = linkedProducts.length > PRODUCTS_COLLAPSE_THRESHOLD;
                    const isOpen = forceOpenSections || !shouldCollapse || productsExpanded;

                    const preview = linkedProducts.slice(0, PRODUCTS_PREVIEW_COUNT);
                    const shown = isOpen ? linkedProducts : preview;
                    const hiddenCount = linkedProducts.length - shown.length;

                    const useScroll = isOpen && !forceOpenSections && linkedProducts.length > 60;

                    return (
                        <section className="mb-3">
                            <div className="flex items-center justify-between gap-3">
        <span className="font-semibold tearsheet-heading">
          {productLabel}:
        </span>

                                {shouldCollapse && !forceOpenSections && (
                                    <button
                                        type="button"
                                        className="text-xs font-semibold text-blue-900 hover:underline"
                                        onClick={() => setProductsExpanded(v => !v)}
                                        aria-expanded={productsExpanded}
                                    >
                                        {productsExpanded ? "Collapse list" : "Show all"}
                                    </button>
                                )}
                            </div>

                            <div
                                className={[
                                    "mt-2",
                                    isOpen
                                        ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2"
                                        : "flex flex-wrap gap-2",
                                    useScroll ? "max-h-64 overflow-auto pr-1" : "",
                                ].join(" ")}
                                role="list"
                                aria-label={`Linked ${productWord(linkedProducts.length)}`}
                            >
                                {shown.map((p, idx) => (
                                    <button
                                        key={p.id ?? idx}
                                        type="button"
                                        role="listitem"
                                        className="text-left px-2 py-1 rounded-full border text-sm font-semibold text-blue-900 hover:bg-gray-50"
                                        title={p.name}
                                        onClick={() => onOpenProductPopup?.(p.id, p.name)}
                                    >
                                        {p.name}
                                    </button>
                                ))}

                                {!isOpen && hiddenCount > 0 && (
                                    <button
                                        type="button"
                                        className="text-xs font-semibold text-blue-900 hover:underline self-center"
                                        onClick={() => setProductsExpanded(true)}
                                        aria-label={`Show ${hiddenCount} more ${productWord(hiddenCount)}`}
                                    >
                                        +{hiddenCount} more
                                    </button>
                                )}
                            </div>

                            {shouldCollapse && !forceOpenSections && (
                                <div className="mt-1 text-xs text-gray-500">
                                    {isOpen
                                        ? `Showing all ${linkedProducts.length} ${productWord(linkedProducts.length)}.`
                                        : `Showing ${shown.length} of ${linkedProducts.length} ${productWord(linkedProducts.length)}.`}
                                </div>
                            )}
                        </section>
                    );
                })()}


                <hr className="my-2" />

                {/* Contact */}
                <div className="mb-2">
                    <h4 className="font-semibold tearsheet-heading">
                        Contact Details:
                    </h4>
                    <div
                        className="mt-2 text-sm"
                        dangerouslySetInnerHTML={createMarkup(contactHtml)}
                    />
                </div>

                <hr className="my-2" />

                {/* Synopsis */}
                <section className="mb-6">
                    <div className="flex items-center justify-between gap-3">
                    <h4 className="font-semibold tearsheet-heading">Synopsis:</h4>
                        {synopsisExpanded && synopsisSectionCount > 0 && (
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    className="view-more synopsis-bulk-toggle"
                                    onClick={() => setSynopsisExpandAll(true)}
                                >
                                    Expand all
                                </button>
                                <button
                                    type="button"
                                    className="view-more synopsis-bulk-toggle"
                                    onClick={() => setSynopsisExpandAll(false)}
                                >
                                    Collapse all
                                </button>
                            </div>
                        )}
                    </div>

                    <div className={`mt-2 text-sm ${!synopsisExpanded ? "line-clamp-5" : ""}`}>
                        <CollapsibleSynopsis
                            rawHtml={synopsisHtml}
                            forceOpenAll={forceOpenSections}
                            interactive={synopsisExpanded}
                            expandAll={synopsisExpandAll}
                            onSectionCountChange={setSynopsisSectionCount}
                            highlight={highlight}
                            sanitize={(html) => createMarkup(html)}
                        />
                    </div>

                    <span className="view-more" onClick={() => setSynopsisExpanded((v) => !v)}>
    {synopsisExpanded ? "View Less" : "View More"}
  </span>
                </section>


                {/* News */}
                {newsHtml &&
                    (
                    <section className="mb-6">
                        <hr className="my-2" />
                        <h4 className="font-semibold tearsheet-heading">News:</h4>
                        <div
                            className={`mt-2 text-sm ${!newsExpanded ? 'line-clamp-3' : ''}`}
                            dangerouslySetInnerHTML={createMarkup(newsHtml)}
                        />
                        <span
                            className="view-more"
                            onClick={() => setNewsExpanded(v => !v)}
                        >
              {newsExpanded ? 'View Less' : 'View More'}
            </span>
                    </section>
                )}



                {/* Additional Info */}
                {additionalInfoHtml && additionalInfoHtml.trim() !== "" &&  additionalInfoHtml.trim() !== "<br>\n<br>" && (
                    <section className="mb-6">
                        <hr className="my-2" />
                        <h4 className="font-semibold tearsheet-heading">
                            Additional Information:
                        </h4>
                        <div
                            className="mt-2 text-sm"
                            dangerouslySetInnerHTML={createMarkup(additionalInfoHtml)}
                        />
                    </section>
                )}

                <hr className="my-2" />

                {/* Strengths & Weaknesses */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                        <h4 className="font-semibold tearsheet-heading">Strengths:</h4>
                        <div
                            className="mt-2 text-sm"
                            dangerouslySetInnerHTML={createListMarkup(strengthsHtml)}
                        />
                    </div>
                    <div>
                        <h4 className="font-semibold tearsheet-heading">Weaknesses:</h4>
                        <div
                            className="mt-2 text-sm"
                            dangerouslySetInnerHTML={createListMarkup(weaknessesHtml)}
                        />
                    </div>
                </div>



                {/* Delivery */}
                {deliveryHtml && (
                    <section className="mb-6">
                        <hr className="my-2" />

                        <h4 className="font-semibold tearsheet-heading">Delivery:</h4>
                        <div
                            className="mt-2 text-sm"
                            dangerouslySetInnerHTML={createMarkup(deliveryHtml)}
                        />
                    </section>
                )}

                <hr className="my-2" />

                <section className="mb-6">
                    <h4 className="font-semibold tearsheet-heading">
                        Product Descriptions:
                    </h4>
                    <div
                        className={`mt-2 text-sm ${!descriptionsExpanded ? 'line-clamp-3' : ''}`}
                        dangerouslySetInnerHTML={createMarkup(descriptionsHtml)}
                    />
                    <span
                        className="view-more"
                        onClick={() => setDescriptionsExpanded(v => !v)}
                    >
            {descriptionsExpanded ? 'View Less' : 'View More'}
          </span>
                </section>

                <NotesAndCommentary vendorId={vendorId} vendorName={vendorName} contextPath={contextPath} />

            </div>
        </div>
    );
}
